import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Ajv2020 from 'ajv/dist/2020';
import { preprocessXAllOf } from '../dsv-schema-utils';
import { generateSamplePayload } from '../sample-generator';
import { Box } from '@dextech-ai/dex-design-system';

const ajv = new Ajv2020({ allErrors: true, strict: false });

export default function ValidationPreview({ schema }) {
  const [payload, setPayload] = useState('');
  const [result, setResult] = useState(null); // { valid: boolean, errors: [] }
  const [parseError, setParseError] = useState(null);
  const [generationNote, setGenerationNote] = useState(null);
  const [pendingOverwrite, setPendingOverwrite] = useState(false);

  const canGenerate = !!(
    schema && schema.type && schema.properties && Object.keys(schema.properties).length > 0
  );

  const computeResult = useCallback((parsed) => {
    const processedSchema = preprocessXAllOf(schema);
    const validate = ajv.compile(processedSchema);
    const valid = validate(parsed);
    return {
      valid,
      errors: valid ? [] : (validate.errors || []).map((err) => ({
        path: err.instancePath || '/',
        message: err.message,
        keyword: err.keyword,
        params: err.params,
      })),
    };
  }, [schema]);

  const runGeneration = useCallback(() => {
    setPendingOverwrite(false);
    const gen = generateSamplePayload(schema);
    if (!gen.payload || typeof gen.payload !== 'object') {
      setParseError('Add fields to the schema first');
      setResult(null);
      setGenerationNote(null);
      return;
    }
    setPayload(JSON.stringify(gen.payload, null, 2));
    setParseError(null);
    const res = computeResult(gen.payload);
    setResult(res);
    if (!res.valid) {
      setGenerationNote(
        `Generated a best-effort sample. ${res.errors.length} constraint(s) couldn't be auto-filled — adjust the fields below.`,
      );
    } else if (gen.unmet.length > 0) {
      setGenerationNote(`Sample generated. Note: ${gen.unmet.join('; ')}`);
    } else {
      setGenerationNote(null);
    }
  }, [schema, computeResult]);

  const handleGenerate = useCallback(() => {
    if (payload.trim()) {
      setPendingOverwrite(true);
      return;
    }
    runGeneration();
  }, [payload, runGeneration]);

  const handleFormat = useCallback(() => {
    if (!payload.trim()) {
      setParseError('Nothing to format');
      return;
    }
    try {
      const parsed = JSON.parse(payload);
      setPayload(JSON.stringify(parsed, null, 2));
      setParseError(null);
    } catch (e) {
      setParseError(`Cannot format — invalid JSON: ${e.message}`);
    }
  }, [payload]);

  const handleValidate = useCallback(() => {
    if (!payload.trim()) {
      setResult(null);
      setParseError('Please enter a JSON payload');
      return;
    }

    let parsed;
    try {
      parsed = JSON.parse(payload);
      setParseError(null);
    } catch (e) {
      setParseError(`Invalid JSON: ${e.message}`);
      setResult(null);
      return;
    }

    if (!schema || !schema.type) {
      setParseError('No valid schema to validate against');
      return;
    }

    try {
      setResult(computeResult(parsed));
    } catch (e) {
      setParseError(`Schema compilation error: ${e.message}`);
      setResult(null);
    }
  }, [payload, schema, computeResult]);

  return (
    <Box py={4} data-testid="validation-preview">
      <Box mb={3}>
        <label className="dsv-modal__label">Test Payload (JSON)</label>
        <textarea
          className="dsv-modal__textarea"
          rows={10}
          value={payload}
          onChange={(e) => {
            setPayload(e.target.value);
            setResult(null);
            setParseError(null);
            setGenerationNote(null);
            setPendingOverwrite(false);
          }}
          placeholder='{"field_name": "value", ...}'
          data-testid="validation-payload-input"
          style={{ fontFamily: 'monospace', fontSize: 13 }}
        />
        <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
          <button
            type="button"
            className="dsv-btn dsv-btn--primary"
            onClick={handleValidate}
            data-testid="validation-run-btn"
          >
            Validate
          </button>
          <button
            type="button"
            className="dsv-btn dsv-btn--ghost"
            onClick={handleFormat}
            data-testid="validation-format-btn"
            title="Parse and pretty-print the pasted JSON"
          >
            Auto Format
          </button>
          <button
            type="button"
            className="dsv-btn dsv-btn--ghost"
            onClick={handleGenerate}
            disabled={!canGenerate}
            data-testid="validation-generate-btn"
            title="Generate a sample payload that satisfies the current schema"
          >
            Generate sample
          </button>
        </div>
        {pendingOverwrite && (
          <div
            data-testid="validation-generate-confirm"
            style={{ marginTop: 8, fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}
          >
            <span>This will replace your current payload.</span>
            <button
              type="button"
              className="dsv-btn dsv-btn--primary"
              onClick={runGeneration}
              data-testid="validation-generate-confirm-ok"
            >
              Replace
            </button>
            <button
              type="button"
              className="dsv-btn dsv-btn--ghost"
              onClick={() => setPendingOverwrite(false)}
              data-testid="validation-generate-cancel"
            >
              Cancel
            </button>
          </div>
        )}
        {generationNote && (
          <div
            data-testid="validation-generate-note"
            style={{ marginTop: 8, fontSize: 13, color: '#b45309' }}
          >
            {generationNote}
          </div>
        )}
      </Box>

      {parseError && (
        <div className="dsv-validation-preview__error" data-testid="validation-parse-error"
          style={{ color: '#dc2626', marginTop: 8, fontSize: 13 }}>
          {parseError}
        </div>
      )}

      {result && (
        <div className="dsv-validation-preview__result" data-testid="validation-result" style={{ marginTop: 12 }}>
          {result.valid ? (
            <div className="dsv-validation-preview__pass" data-testid="validation-pass"
              style={{ color: '#16a34a', fontWeight: 600, fontSize: 14 }}>
              Payload is valid
            </div>
          ) : (
            <div className="dsv-validation-preview__fail">
              <div data-testid="validation-fail"
                style={{ color: '#dc2626', fontWeight: 600, fontSize: 14, marginBottom: 8 }}>
                Validation failed ({result.errors.length} error{result.errors.length !== 1 ? 's' : ''})
              </div>
              <ul className="dsv-validation-preview__error-list" style={{ margin: 0, padding: '0 0 0 20px', fontSize: 13 }}>
                {result.errors.map((err, i) => (
                  <li key={i} data-testid={`validation-error-${i}`} style={{ marginBottom: 4 }}>
                    <code style={{ color: '#9333ea' }}>{err.path || '/'}</code>: {err.message}
                    {err.keyword && <span style={{ color: '#6b7280' }}> ({err.keyword})</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Box>
  );
}

ValidationPreview.propTypes = {
  schema: PropTypes.object,
};
